import {
  FilterItem,
  FilterItemOperator,
  FilterItemOperatorTypes,
} from '@mm/state/model/AudiencesModel';
import classNames from 'classnames';
import React, { ReactNode, useMemo } from 'react';
import PrimarySelect, { DropdownOption } from '../PrimarySelect/PrimarySelect';
import {
  AudienceFilterItemInputChildProps,
  MULTI_VALUES_OPERATORS,
} from './AudienceFilterItemInput';
import { ReactComponent as Equal } from './icons/equal.svg';
import { ReactComponent as NotEqual } from './icons/not-equal.svg';
import { ReactComponent as Contains } from './icons/contains.svg';
import { ReactComponent as DoesNotContain } from './icons/does-not-contain.svg';
import { ReactComponent as EndsWith } from './icons/ends-with.svg';
import { ReactComponent as StartsWith } from './icons/starts-with.svg';
import { ReactComponent as GreaterThan } from './icons/greater-than.svg';
import { ReactComponent as LessThan } from './icons/less-than.svg';
import { ReactComponent as Exists } from './icons/exists.svg';
import {
  MyLeadsFilterTypes,
  MY_LEADS_OPERATORS_BY_TYPE,
} from '@mm/state/interfaces/myLeads';

export const AudienceFilterItemInputOperator = ({
  filter,
  setFilter,
  variableOperators,
  fetchingVariableOperators,
  busy,
  className,
  css,
  withIcons,
}: AudienceFilterItemInputChildProps) => {
  const items = useMemo(
    () =>
      variableOperators
        ? variableOperators?.variableOperators?.map((item, i) => ({
            ...item,
            id: i,
          }))
        : [],
    [variableOperators],
  );

  const customOptionRenderer = (
    option: DropdownOption,
    withIcons?: boolean,
  ) => {
    let icon: ReactNode;

    switch (option.value) {
      case FilterItemOperatorTypes.EQUAL:
        icon = <Equal />;
        break;
      case FilterItemOperatorTypes.NOT_EQUAL:
        icon = <NotEqual />;
        break;
      case FilterItemOperatorTypes.GREATER_THAN:
        icon = <GreaterThan />;
        break;
      case FilterItemOperatorTypes.LESS_THAN:
        icon = <LessThan />;
        break;
      case FilterItemOperatorTypes.CONTAINS:
        icon = <Contains />;
        break;
      case FilterItemOperatorTypes.DOESN_NOT_CONTAIN:
        icon = <DoesNotContain />;
        break;
      case FilterItemOperatorTypes.STARTS_WITH:
        icon = <StartsWith />;
        break;
      case FilterItemOperatorTypes.ENDS_WITH:
        icon = <EndsWith />;
        break;
      case FilterItemOperatorTypes.EXISTS:
        icon = <Exists />;
        break;
      default:
        '';
    }

    if (withIcons) {
      return (
        <span className="audience-filter-item-input__option-with-icon">
          {icon}
          {option.name}
        </span>
      );
    }

    return <span>{option.name}</span>;
  };
  const selectedItem = items?.find(i => i.value === filter.operator);

  const onChangeOperator = (operator: FilterItemOperator) => {
    let filterUpdates: Partial<FilterItem> = { operator };

    const leadsType = Object.entries(MY_LEADS_OPERATORS_BY_TYPE).find(
      entry => entry[1] === operator,
    )?.[0];

    const operatorTakesMultiValues = MULTI_VALUES_OPERATORS.includes(operator);
    if (operatorTakesMultiValues !== Array.isArray(filter.value)) {
      filterUpdates = {
        ...filterUpdates,
        value: operatorTakesMultiValues ? [] : '',
      };
    }
    setFilter({
      ...filter,
      ...filterUpdates,
      ...(filter.esType &&
        leadsType && {
          esType: filter.esType,
        }),
      operator,
    });
  };

  return (
    <div
      className={classNames(
        'audience-filter-item-input__section',
        css?.section,
        {
          [`${className}__section`]: className,
        },
      )}
    >
      <div
        className={classNames(
          'audience-filter-item-input__field-title',
          css?.fieldTitle,
          {
            [`${className}__field-title`]: className,
          },
        )}
      >
        Operator
      </div>
      <PrimarySelect
        placeholder={
          fetchingVariableOperators ? (
            <>
              <i className="fas fa-spinner fa-spin" /> Loading operators
            </>
          ) : undefined
        }
        className={classNames(
          'audience-filter-item-input__primary-select',
          css?.select,
          {
            [`${className}__selector`]: className,
          },
        )}
        activeItemClassName={classNames(css?.selectActiveItem, {
          [`${className}__operator-selector__active-item`]: className,
        })}
        value={selectedItem}
        onChange={item => onChangeOperator(item.value)}
        disabled={fetchingVariableOperators}
        options={items}
        customDropdownStyles={{ zIndex: '1000010' }}
        renderWithoutPopover
        customOptionRenderer={option => customOptionRenderer(option, withIcons)}
      />
    </div>
  );
};
